import axios from 'axios';
import { getAccessToken, clearUserData } from './auth';
import { encryptJSON, decryptJSON } from './encryptionUtils';
import {
  isAuthSessionExpired,
  redirectToLoginAfterAuthFailure,
  rejectIfAuthSessionExpired,
} from './authSessionGuard';

const ENCRYPTED_LOGIN_ENDPOINTS = [
  '/suite/login/',
];

const shouldEncryptAllPayloads = (): boolean => {
  return process.env.REACT_APP_ENCRYPT_ALL_PAYLOAD?.toLowerCase() === 'true';
};

const shouldDecryptAllResponses = (): boolean => {
  return process.env.REACT_APP_DECRYPT_ALL_RESPONSE?.toLowerCase() === 'true';
};

const requiresEncryption = (url: string): boolean => {
  if (shouldEncryptAllPayloads()) {
    return true;
  }
  return ENCRYPTED_LOGIN_ENDPOINTS.some(pattern => url.includes(pattern));
};

const requiresDecryption = (url: string): boolean => {
  if (shouldDecryptAllResponses()) {
    return true;
  }
  return ENCRYPTED_LOGIN_ENDPOINTS.some(pattern => url.includes(pattern));
};

const isLoginUrl = (url: string): boolean =>
  ENCRYPTED_LOGIN_ENDPOINTS.some((pattern) => url.includes(pattern)) ||
  url.includes('/login');

const createAuthAxios = (baseURL?: string) => {
  const instance = axios.create({
    baseURL:
      baseURL ||
      process.env.REACT_APP_API_BASE_URL,
  });

  instance.interceptors.request.use(
    (config) => {
      const url = config.url || '';
      // Allow login endpoints even after a prior expiry (new session).
      if (!isLoginUrl(url)) {
        const blocked = rejectIfAuthSessionExpired();
        if (blocked) {
          return Promise.reject(blocked);
        }
      }

      const token = getAccessToken();
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }

      const method = config.method?.toLowerCase();
      const hasPayload = config.data && (method === 'post' || method === 'put' || method === 'patch' || method === 'delete');

      const isFormData =
        typeof FormData !== "undefined" && config.data instanceof FormData;
      if (isFormData && config.headers) {
        const headers = config.headers as {
          delete?: (name: string) => void;
          [key: string]: unknown;
        };
        if (typeof headers.delete === "function") {
          headers.delete("Content-Type");
        } else {
          delete headers["Content-Type"];
          delete headers["content-type"];
        }
      }
      if (requiresEncryption(url) && hasPayload && !isFormData) {
        try {
          const encryptedData = encryptJSON(config.data);
          config.data = { data: encryptedData };
        } catch (error: any) {
          throw new Error(`Request encryption failed: ${error.message}`);
        }
      }

      return config;
    },
    (error) => {
      return Promise.reject(error);
    }
  );

  instance.interceptors.response.use(
    (response) => {
      const url = response.config.url || '';

      if (requiresDecryption(url) && response.data) {
        if (
          typeof response.data === 'object' &&
          'data' in response.data &&
          typeof response.data.data === 'string'
        ) {
          try {
            const decryptedData = decryptJSON(response.data.data);
            response.data = decryptedData;
          } catch (error) {
          }
        }
      }

      return response;
    },
    (error) => {
      // Only 401 means the JWT/session is dead. Business 403 (e.g. missing
      // role or pause auth) must not clear storage and bounce to login.
      if (error.response?.status === 401) {
        const url = String(error.config?.url || '');
        // Don't treat failed login as "session expired" for the whole app.
        if (!isLoginUrl(url) && !isAuthSessionExpired()) {
          redirectToLoginAfterAuthFailure(clearUserData);
        } else if (!isLoginUrl(url)) {
          try {
            clearUserData();
          } catch {
            /* ignore */
          }
        }
      }
      return Promise.reject(error);
    }
  );

  return instance;
};

/** Live-session API host (join → exit). Falls back to business API base. */
const livekitApiBaseUrl = (): string | undefined =>
  process.env.REACT_APP_LIVEKIT_API_BASE_URL ||
  process.env.REACT_APP_API_BASE_URL;

const makeService = (resolveBase: () => string | undefined) => ({
  get: async (url: string, config?: any) => {
    const authAxios = createAuthAxios(resolveBase());
    return authAxios.get(url, config);
  },
  post: async (url: string, data?: any, config?: any) => {
    const authAxios = createAuthAxios(resolveBase());
    return authAxios.post(url, data, config);
  },
  put: async (url: string, data?: any, config?: any) => {
    const authAxios = createAuthAxios(resolveBase());
    return authAxios.put(url, data, config);
  },
  delete: async (url: string, data?: any, config?: any) => {
    const authAxios = createAuthAxios(resolveBase());
    if (data) {
      const deleteConfig = { ...config, data };
      return authAxios.delete(url, deleteConfig);
    }
    return authAxios.delete(url, config);
  },
  patch: async (url: string, data?: any, config?: any) => {
    const authAxios = createAuthAxios(resolveBase());
    return authAxios.patch(url, data, config);
  },
});

// Auth service for making authenticated API calls (business LMS)
export const authService = makeService(
  () => process.env.REACT_APP_API_BASE_URL,
);

/** Join → exit live-session APIs (tokens, recording, moderate, in-room session). */
export const livekitAuthService = makeService(livekitApiBaseUrl);

export default authService;
